import React from 'react';
import PropTypes from 'prop-types';
import css from '../PreferencesSection.scss';
import aptCss from './AppointmentPreferences.scss';
import Checkbox from 'SF/Checkbox';
import {
    alertMinutesBeforeOptions, 
    reminderDayOptions
} from '../selectionOptions';
import PreferencesSection from '../PreferencesSection';
import HourlyDropdown from 'common/HourlyDropdown';
import Select from 'common/StyledSelect';
import { find } from 'lodash';
import { ifFieldEmptyReturnDefault } from 'utils/text';

/**
 * @param {object} props - see propTypes
 * @returns {node} Render's a user's appointment preferences to display in their profile. This only renders various components
 * when the correct permissions exist.
 */
const AppointmentPreferences = (props) => {
    return (
        <PreferencesSection className={aptCss['appointment-options']}
                            headerText="Appointments"
                            descriptionText="Customize the timing of your appointment reminders.">
            <div>
                <Checkbox ariaLabel="Remind me before the start of the appointment"
                          checked={props.sendAlert}
                          onChange={(checked) => props.setValue({sendAlert: checked})} />
                <div className={css['profile-adv-options']} id="alertPreferences">
                    <label>Remind me <u className={css['bold']}>before the start</u>:</label>
                    <br />
                    <Select id="sendAlertMinutes"
                            ariaLabel="Remind me before the start of the appointment (minutes)"
                            className={aptCss['dropdown']}
                            isDisabled={!props.sendAlert}
                            onChange={minutes => props.setValue({ sendAlertTime: minutes.value })}
                            options={alertMinutesBeforeOptions}
                            value={ifFieldEmptyReturnDefault(find(alertMinutesBeforeOptions, ['value', props.sendAlertTime]),
                                find(alertMinutesBeforeOptions, ['value', 15]))} />
                </div>
            </div>
            <div>
                <Checkbox aria-controls="reminderPreferences"
                          ariaLabel=""
                          checked={props.sendReminder}
                          data-testid="sendReminder-checkbox"
                          onChange={(checked) => props.setValue({sendReminder: checked})} />
                <div className={css['profile-adv-options'] + ' ' + aptCss['reminder-preferences']} id="reminderPreferences">
                    <label>Remind me of the appointment:</label>
                    <br />
                    <HourlyDropdown id="reminderHour"
                                    ariaLabel="Remind me of the appointment (hour)"
                                    isDisabled={!props.sendReminder}
                                    onChange={(hour) => props.setValue({ reminderTime: hour })}
                                    value={props.reminderTime} />
                    <Select id="reminderDay"
                            ariaLabel="Remind me of the appointment (day)"
                            className={aptCss['dropdown']}
                            isDisabled={!props.sendReminder}
                            onChange={day => props.setValue({ reminderWhen: day.value })} 
                            options={reminderDayOptions}
                            value={find(reminderDayOptions, ['value', props.reminderWhen])} />
                </div>
            </div>
            { props.weekendReminderEmailSettingEnabled &&
                <>
                    <h3>Weekend Appointment Reminders</h3>
                    <div>
                        <Checkbox ariaLabel=""
                                  aria-labelledby="weekendRemindersLabel"
                                  checked={props.weekendRemindersOff}
                                  onChange={(checked) => props.setValue({weekendRemindersOff: checked})} />
                        <div className={css['profile-adv-options']}>
                            <label id="weekendRemindersLabel">
                                I <span className={css['bold']}>do not</span> want to receive reminders during the weekend.
                                <br/>
                                <span className={css['profile-sub-text']}>When selected, appointment notifications for Saturday, 
                                    Sunday, or Monday will be sent on the Friday prior to those appointments.</span>
                            </label>
                        </div>
                    </div>
                </>
            }
        </PreferencesSection>
    );
};

AppointmentPreferences.propTypes = {
    reminderTime: PropTypes.string,
    reminderWhen: PropTypes.string,
    sendAlert: PropTypes.bool,
    sendAlertTime: PropTypes.number,
    sendReminder: PropTypes.bool,
    weekendRemindersOff: PropTypes.bool,
    setValue: PropTypes.func.isRequired,
    weekendReminderEmailSettingEnabled: PropTypes.bool.isRequired
};

export default AppointmentPreferences;
